import { Modal } from '@shared/components/ui/Modal';
import { Button } from '@shared/components/ui/Button';
import { Lock, Check, Zap, Star, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureName?: string;
  requiredTier?: 'starter' | 'growth' | 'accelerator';
}

export function PaywallModal({ isOpen, onClose, featureName = 'this feature', requiredTier }: PaywallModalProps) {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  // ADMIN BYPASS: Don't show paywall for admins
  if (isAdmin) {
    return null;
  }

  const handleUpgrade = () => {
    onClose();
    navigate('/subscription');
  };

  const tiers = [
    {
      name: 'Starter',
      slug: 'starter',
      price: 0,
      icon: Star,
      color: 'blue',
      features: [
        '3 Career Reports',
        'Basic Visualizations',
        'PDF Export',
        'Profile Management'
      ]
    },
    {
      name: 'Growth',
      slug: 'growth',
      price: 29,
      icon: Zap,
      color: 'purple',
      popular: true,
      features: [
        'Unlimited Career Reports',
        'Skills Tracker',
        '50 AI Coach Messages/month',
        'Voice Interactions',
        'Advanced Visualizations'
      ]
    },
    {
      name: 'Accelerator',
      slug: 'accelerator',
      price: 45,
      icon: Crown,
      color: 'amber',
      features: [
        'Everything in Growth',
        'Unlimited Job Match AI',
        'Interview Prep Tools',
        'Priority Support',
        'Advanced Analytics'
      ]
    }
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
          <Lock className="text-blue-600" size={32} />
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Unlock {featureName}
        </h2>

        <p className="text-gray-600 mb-6">
          Choose a plan to access this feature and unlock your career potential
        </p>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800 font-medium">
            One-time setup fee of $197 required for all plans
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 mb-6 text-left">
          {tiers.map((tier) => {
            const Icon = tier.icon;
            const isRequired = requiredTier ? tier.slug === requiredTier ||
              (tier.slug === 'growth' && requiredTier === 'starter') ||
              (tier.slug === 'accelerator' && (requiredTier === 'starter' || requiredTier === 'growth'))
              : true;

            return (
              <div
                key={tier.slug}
                className={`border-2 rounded-lg p-4 relative ${
                  tier.popular
                    ? 'border-purple-500 bg-purple-50'
                    : isRequired
                    ? 'border-blue-200 bg-blue-50'
                    : 'border-gray-200 bg-gray-50 opacity-60'
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-purple-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon className={`text-${tier.color}-600`} size={24} />
                    <h3 className="text-lg font-bold text-gray-900">{tier.name}</h3>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-600 mb-1">Setup: $197</div>
                    <div className="text-2xl font-bold text-gray-900">
                      ${tier.price}
                    </div>
                    <div className="text-xs text-gray-600">/month</div>
                  </div>
                </div>

                <ul className="space-y-2 mb-4">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-sm">
                      <Check className="text-green-600 mr-2 flex-shrink-0 mt-0.5" size={16} />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {!isRequired && (
                  <div className="text-xs text-gray-500 italic">
                    Not sufficient for this feature
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex gap-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
          >
            Maybe Later
          </Button>
          <Button
            onClick={handleUpgrade}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
          >
            View All Plans
          </Button>
        </div>
      </div>
    </Modal>
  );
}
